import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import { quoteRequestService } from '@/services/QuoteRequestService'
import { vendorService } from '@/services/VendorService'
import { quoteKeys, vendorKeys } from '@/hooks/queryKeys'
import type { EventQuoteRequestListDto, QuoteRequestDto, VendorQuoteRequestListDto } from '@lib/dto/quote-request.dto'
import type { CreateQuoteRequestInput } from '@lib/input/quote-request.input'
import type { VendorDto } from '@lib/dto/vendor.dto'

/** Ask a vendor for a quote for this event. */
export function useSendQuoteRequest(eventId: string): UseMutationResult<QuoteRequestDto, Error, CreateQuoteRequestInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input) => quoteRequestService.send(eventId, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: quoteKeys.event(eventId) }),
  })
}

/** The event's quote requests: a couple's own, or all of them for its planner and the venue's staff. */
export function useEventQuoteRequests(eventId: string): UseQueryResult<EventQuoteRequestListDto> {
  return useQuery({ queryKey: quoteKeys.event(eventId), queryFn: () => quoteRequestService.forEvent(eventId) })
}

/** The signed-in vendor company's requests, a page at a time. */
export function useOwnQuoteRequests(page: number): UseQueryResult<VendorQuoteRequestListDto> {
  return useQuery({ queryKey: quoteKeys.own(page), queryFn: () => quoteRequestService.own(page), placeholderData: keepPreviousData })
}

/** Choose who at the company gets its quote requests by email; null for its owners. */
export function useSetQuoteRecipient(): UseMutationResult<VendorDto, Error, string | null> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId) => vendorService.setQuoteRecipient(userId),
    onSuccess: (vendor) => queryClient.setQueryData(vendorKeys.self(), vendor),
  })
}
